import type { AppConfig } from '@/types/config'

export const mxBooksConfig: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
    appUrl: import.meta.env.VITE_APP_URL,
    version: 'v1'
  },
  app: {
    id: 'mx-books',
    name: 'Sherlock Holmes Books',
    description: 'Discover and read your favorite Sherlock Holmes books',
    contentId: 6,
    subscriptionPlanId: 5,
    logo: {
      default: '/assets/images/mx-books/logo.svg',
      icon: '/assets/images/mx-books/icon.svg'
    }
  },

  theme: {
    colors: {
      primary: '#914349', // Burgundy/maroon - primary brand color from mxpublishing.com (for CTA buttons)
      secondary: '#545454', // Medium gray - body text color from mxpublishing.com
      accent: '#914349', // Burgundy/maroon - primary brand accent color from mxpublishing.com
      background: '#ffffff' // White background from mxpublishing.com
    },
    dark: {
      primary: '#914349', // Same burgundy for CTA buttons in both modes
      secondary: '#d1d1d1', // Light gray for secondary text
      accent: '#914349', // Same burgundy accent for CTA buttons in both modes
      background: '#1a1a1a' // Dark background
    },
    fonts: {
      heading: 'Inter',
      body: 'Inter'
    },
    form: {
      padding: {
        container: 'py-8 px-4 sm:px-10',
        field: 'space-y-4'
      },
      rounding: {
        button: 'rounded-md',
        input: 'rounded-md'
      }
    }
  },

  layout: {
    maxWidth: '2xl',
    headerHeight: '16',
    playerHeight: '72px',
    screens: {
      home: [
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Featured Books",
            "list": [],
            "params": {
              "filter[category]": "featured"
            }
          },
          "title": "Featured Books",
          "limit": 20
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "New Releases",
            "list": [],
            "params": {
              "filter[category]": "new"
            }
          },
          "title": "New Releases"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Included in Subscription",
            "list": [],
            "params": {
              "filter[is_subscription]": 1
            }
          },
          "title": "Included in Subscription"
        },
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Browse by Genre",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Browse by Genre",
          "limit": 8
        },
        {
          "type": "authors",
          "layout": "horizontal",
          "data": {
            "title": "Popular Authors",
            "list": [],
            "params": {
              "limit": 30
            }
          },
          "title": "Popular Authors"
        }
      ],
      search: [
        {
          "type": "authors",
          "layout": "two_column",
          "data": {
            "title": "Popular Authors",
            "list": [],
            "params": {
            }
          },
          "title": "Popular Authors"
        },
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Browse by Genre",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Browse by Genre",
          "limit": 8
        }
      ],
      library: [
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "recently_played",
          "data": {
            "title": "Recently Read",
            "list": [],
            "params": {
              "filter[library]": "played"
            }
          },
          "title": "Recently Read"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "liked",
          "data": {
            "title": "Liked",
            "list": [],
            "params": {
              "filter[library]": "liked"
            }
          },
          "title": "Liked"
        },
        {
          "type": "titles",
          "layout": "grid",
          "id": "purchased",
          "data": {
            "title": "My Library",
            "list": [],
            "params": {
              "filter[library]": "purchased"
            }
          },
          "title": "My Library"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "bookmarked",
          "data": {
            "title": "Bookmarked",
            "list": [],
            "params": {
              "filter[library]": "bookmarked"
            }
          },
          "title": "Bookmarked"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "finished",
          "data": {
            "title": "Finished",
            "list": [],
            "params": {
              "filter[library]": "finished"
            }
          },
          "title": "Finished"
        }
      ]
    }
  },

  features: {
    shop: true,
    ads: true,
    guestMode: true,
    favorites: true,
    darkMode: true,
    search: true,
    player: false,
    sharing: true,
    epubReader: true,
    video: false,
    hasPreview: true,
  },

  defaultMode: 'light' as const,

  subscription: {
    stripePlanId: 'price_mx_books_monthly',
    price: '4.99',
    currency: 'GBP',
    features: [
      'High Quality Books',
      'Offline Access',
      'Ad Free Experience',
      'No Commitment',
      'Cancel Anytime'
    ],
    usps: [
      {
        title: 'Premium Reading Experience',
        description: 'Enjoy all books with high-quality formatting and typography for the best reading experience.',
        icon: 'book'
      },
      {
        title: 'Offline Reading',
        description: 'Download your favorite books and read them even without internet connection.',
        icon: 'download'
      },
      {
        title: 'Ad-Free Experience',
        description: 'Enjoy uninterrupted reading with no ads or promotions.',
        icon: 'like'
      },
      {
        title: 'Exclusive Content',
        description: 'Get access to subscriber-only books, early releases, and original publications.',
        icon: 'subscribe'
      }
    ]
  },

  urls: {
    terms: 'https://mxpublishing.com/pages/terms-and-conditions',
    privacy: 'https://mxpublishing.com/pages/privacy',
    support: 'https://mxpublishing.com/pages/contact-us'
  },

  seo: {
    title: 'MX Books - Your Personal Book Library',
    description: 'Discover and read your favorite books',
    ogImage: '/assets/images/mx-books/og-image.svg',
    defaults: {
      titles: {
        title: 'Books | MX Books',
        description: 'Explore our collection of premium books',
        type: 'article'
      },
      categories: {
        title: 'Categories | MX Books',
        description: 'Browse books by genre and category',
        type: 'website'
      },
      authors: {
        title: 'Authors | MX Books',
        description: 'Discover talented authors and their works',
        type: 'website'
      },
      series: {
        title: 'Series | MX Books',
        description: 'Follow your favorite book series',
        type: 'website'
      },
      casts: {
        title: 'Narrators | MX Books',
        description: 'Meet the talented narrators behind our audiobooks',
        type: 'website'
      }
    }
  }
}

